import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LANGS = ["de", "en", "it", "ja", "zh"];

function Monogram({ className = "" }) {
  // Real Madubau bee monogram — high-res transparent PNG (client-supplied)
  return (
    <img
      src="https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/lwvvm216_49fc08fe-7883-4f7d-b033-469f1b929ab7.png"
      alt="Madubau"
      className={className}
      style={{ objectFit: "contain", objectPosition: "center 20%" }}
    />
  );
}

export default function Header() {
  const { lang, setLang, t } = useI18n();
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const nav = [
    { to: "/shop", label: t.nav.shop },
    { to: "/story", label: t.nav.story },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/gift-card", label: t.nav.giftcard, gold: true },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center text-cream">
          <img
            src="/logo-v2.png?v=2"
            alt="Madubau Dolomiti"
            className="h-14 sm:h-16 w-auto shrink-0"
            data-testid="logo-full"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `font-display text-[11px] tracking-[0.28em] link-underline ${
                  n.gold
                    ? (isActive ? "text-gold-light" : "text-gold hover:text-gold-light")
                    : (isActive ? "text-gold" : "text-cream/80 hover:text-gold")
                } transition-colors`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden sm:flex items-center gap-1 text-[10px] font-display tracking-[0.25em]">
            {LANGS.map((l, i) => (
              <span key={l} className="flex items-center">
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-1 transition-colors ${lang === l ? "text-gold" : "text-cream/50 hover:text-cream"}`}
                >
                  {l.toUpperCase()}
                </button>
                {i < LANGS.length - 1 && <span className="text-cream/20">·</span>}
              </span>
            ))}
          </div>

          <Link to={user ? "/account" : "/login"} data-testid="account-link" className="text-cream/80 hover:text-gold transition-colors">
            <User className="w-4 h-4" />
          </Link>

          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="text-cream/80 hover:text-gold transition-colors relative"
          >
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-gold text-black text-[9px] font-medium w-4 h-4 flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-cream"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-black border-t border-white/5"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className="font-display text-sm tracking-[0.28em] text-cream/80"
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`font-display text-[11px] tracking-[0.28em] ${lang === l ? "text-gold" : "text-cream/50"}`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
